import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOptionalVmClient } from '@/api/context';
import { useAppSession } from '@/contexts/SessionContext';
import { Urls } from '@/lib/navigation';

/**
 * Hydrates the active chat for /agent/<id>: pulls from cache when available,
 * otherwise fetches the session and timeline from the bridge. Redirects home
 * if `id` is missing or the session can't be loaded. Returns a `loading`
 * flag that the caller can gate its render on.
 */
export const useConversationHydration = (id: string | undefined): boolean => {
  const { session } = useAppSession();
  const { chat, chatHistory } = session;
  const navigate = useNavigate();
  const client = useOptionalVmClient();

  const [loading, setLoading] = useState(true);
  const loadingIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!id) {
      navigate(Urls.home(), { replace: true });
      return;
    }
    if (!client) {
      setLoading(true);
      return;
    }

    const cached = chat.getChat(id);
    if (cached && (cached.timeline.length > 0 || cached.isRunning)) {
      chat.hydrateChat(id);
      void chat.reconnectIfNeeded(id);
      setLoading(false);
      return;
    }

    loadingIdRef.current = id;
    setLoading(true);

    void (async () => {
      try {
        await client.getSession(id);
      } catch {
        navigate(Urls.home(), { replace: true });
        return;
      }

      try {
        const timeline = await chatHistory.loadSessionTimeline(id);
        if (loadingIdRef.current !== id) return;
        chat.hydrateChat(id, { timeline });
      } catch {
        if (loadingIdRef.current !== id) return;
        navigate(Urls.home(), { replace: true });
        return;
      }

      void chat.reconnectIfNeeded(id);
      if (loadingIdRef.current === id) {
        setLoading(false);
      }
    })();

    return () => {
      loadingIdRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, client]);

  return loading;
};
